import React, { useEffect, useState } from "react";
import { CalendarDays, CircleCheck, Clock3, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./OverviewCards.css";

function useCountUp(target) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const finalValue = Number.isFinite(Number(target)) ? Number(target) : 0;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setValue(finalValue);
      return undefined;
    }

    let frameId;
    const start = performance.now();
    const duration = 550;
    const animate = (time) => {
      const progress = Math.min(1, (time - start) / duration);
      setValue(finalValue * (1 - (1 - progress) ** 3));
      if (progress < 1) frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frameId);
  }, [target]);

  return value;
}

function Sparkline({ values = [], color }) {
  const series = values.length ? values : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(1, ...series);
  const points = series
    .map((value, index) => `${(index / Math.max(1, series.length - 1)) * 100},${30 - (Number(value || 0) / max) * 26}`)
    .join(" ");

  return (
    <svg className="oc__sparkline" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MetricCard({ card, stats }) {
  const { t } = useTranslation("cabinetDashboard");
  const revenue = card.valueKey === "revenue";
  const rawValue = revenue ? stats.revenueValue : stats[card.valueKey];
  const parsedValue = Number(rawValue);
  const numericTarget = Number.isFinite(parsedValue) ? parsedValue : 0;
  const count = useCountUp(numericTarget);
  const displayValue = revenue
    ? `${new Intl.NumberFormat(t("dashboard.locale", { defaultValue: "fr-FR" }), { maximumFractionDigits: 3 }).format(count)} TND`
    : Math.round(count);
  const trendColor = card.trendColor(stats);
  const trend = stats[card.subKey] || card.staticSub;

  return (
    <article className="oc__card" aria-label={`${card.label}: ${displayValue}`}>
      {card.live && Number(stats.waiting) > 0 && (
        <span className="oc__live">
          <span className="oc__live-dot" aria-hidden="true" />
          LIVE
        </span>
      )}
      <span className="oc__icon" style={{ background: card.iconBg, color: card.iconColor }}>
        {card.icon}
      </span>
      <span className="oc__value">{displayValue}</span>
      <span className="oc__label">{card.label}</span>
      <Sparkline values={stats.sparklines?.[card.valueKey]} color={card.iconColor} />
      {trend && (
        <span className="oc__sub" style={{ color: trendColor }}>
          {trend}
        </span>
      )}
    </article>
  );
}

export default function OverviewCards({ stats = {} }) {
  const { t } = useTranslation("cabinetDashboard");
  const cards = [
    {
      id: "appointments",
      icon: <CalendarDays aria-hidden="true" />,
      iconBg: "#e8f1ff",
      iconColor: "#006eea",
      valueKey: "appointments",
      label: t("dashboard.overview.appointments"),
      subKey: "appointmentsDelta",
      trendColor: (current) => Number(current.appointmentsDeltaValue) >= 0 ? "#0b8d67" : "#c63d4a",
    },
    {
      id: "waiting",
      icon: <Clock3 aria-hidden="true" />,
      iconBg: "#eff6ff",
      iconColor: "#3b82f6",
      valueKey: "waiting",
      label: t("dashboard.overview.waiting"),
      staticSub: t("dashboard.overview.realtime"),
      trendColor: () => "#6b7280",
      live: true,
    },
    {
      id: "completed",
      icon: <CircleCheck aria-hidden="true" />,
      iconBg: "#dbeafe",
      iconColor: "#006eea",
      valueKey: "completed",
      label: t("dashboard.overview.completed"),
      subKey: "completionRate",
      trendColor: () => "#0b8d67",
    },
    {
      id: "revenue",
      icon: <Wallet aria-hidden="true" />,
      iconBg: "#e0e7ff",
      iconColor: "#1d4ed8",
      valueKey: "revenue",
      label: t("dashboard.overview.revenue"),
      subKey: "revenueDelta",
      trendColor: (current) => Number(current.revenueDeltaValue) >= 0 ? "#0b8d67" : "#c63d4a",
    },
  ];

  return (
    <section className="oc" aria-labelledby="oc-title">
      <h2 className="oc__title" id="oc-title">{t("dashboard.overview.title")}</h2>
      <div className="oc__grid">
        {cards.map((card) => <MetricCard key={card.id} card={card} stats={stats} />)}
      </div>
    </section>
  );
}
